import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AutoSizer, Table, Column, InfiniteLoader } from 'react-virtualized';
import dayjs from 'dayjs';
import { Spin, Tooltip, Empty } from 'antd';
import classNames from 'classnames';
import { ApplicationState } from '../../../reducers';
import { getPageLimit, getTimezoneOffset } from '../../../utils/Utils';
import { fetchAPIToken } from '../../../actions/distribution_apisettings';
import { useAppDispatch } from '../../../store/ReduxStore';
import { AsyncDispatch } from '../../../../types/global';
import ApiRequestDetailsModal from './ApiRequestDetailsModal';

const ApiSettingsTable: React.FC = () => {
  const [pageSize] = React.useState<number>(getPageLimit());
  const [showModal, setShowModal] = React.useState(false);
  const [selectedParams, setSelectedParams] = React.useState<string | null>(null);
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const { apiSettings, fetchingLogs, selectedTokenId } = useSelector(
    (state: ApplicationState) => ({
      apiSettings: state.distribution.apiSettings,
      fetchingLogs: state.distribution.apiSettings.fetchingLogs,
      selectedTokenId: state.distribution.apiSettings.selectedTokenId,
    }),
    shallowEqual
  );

  const timezoneOffset = React.useMemo(() => getTimezoneOffset(), []);

  const handleScroll = ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
    const nextPage = Math.ceil(stopIndex / pageSize);
    const currentPage = Math.ceil(startIndex / pageSize);
    const lastPage = currentPage > startIndex / pageSize;
    if (!lastPage) {
      if (!selectedTokenId || selectedTokenId <= 0) return Promise.resolve();
      return dispatch(fetchAPIToken({ tokenId: selectedTokenId, page: nextPage, limit: pageSize }));
    }
    return Promise.resolve();
  };

  const noData = () => {
    if (fetchingLogs && apiSettings.logs.length === 0)
      return (
        <Spin
          spinning={fetchingLogs && apiSettings.logs.length === 0}
          size="large"
          className="api-settings__log-spinner"
        />
      );

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  };

  return (
    <AutoSizer>
      {({ height, width }) => (
        <div style={{ height, width }}>
          <div className="api-settings__log-table">
            <InfiniteLoader
              isRowLoaded={({ index }) => !!apiSettings.logs[index]}
              loadMoreRows={handleScroll}
              rowCount={apiSettings.logs.length + pageSize}
              minimumBatchSize={pageSize}
            >
              {({ onRowsRendered, registerChild }) => (
                <React.Fragment>
                  <Table
                    height={height - 12}
                    width={width - 2}
                    onRowsRendered={onRowsRendered}
                    ref={registerChild}
                    headerClassName="api-settings__log-table-header"
                    rowClassName="api-settings__log-table-row"
                    headerHeight={50}
                    rowCount={apiSettings.logs.length}
                    dataSource={apiSettings.logs!}
                    rowHeight={40}
                    rowGetter={({ index }) => apiSettings.logs[index]}
                    noRowsRenderer={noData}
                  >
                    <Column
                      label={t('api:status')}
                      dataKey="code"
                      width={100}
                      cellRenderer={({ cellData, rowData }) => (
                        <Tooltip title={rowData.status} placement="right">
                          <span
                            className={classNames({
                              green: cellData === '200',
                              red: cellData !== '200',
                              yellow: cellData === 'pending',
                            })}
                          >
                            {cellData}
                          </span>
                        </Tooltip>
                      )}
                    />
                    <Column
                      label={`${t('api:timeStamp')} (${timezoneOffset})`}
                      dataKey="started_at"
                      width={250}
                      cellRenderer={({ cellData }) => (
                        <div>{dayjs(cellData).local().format('DD.MM.YYYY, h:mm a')}</div>
                      )}
                    />
                    <Column
                      label={t('api:requestDetails')}
                      dataKey="params"
                      width={350}
                      flexGrow={1}
                      cellRenderer={({ cellData }) => (
                        <div
                          onClick={() => {
                            setSelectedParams(cellData);
                            setShowModal(true);
                          }}
                          className="api-settings__log-params-cell"
                        >
                          {cellData}
                        </div>
                      )}
                    />
                    <Column label={t('api:apiEndPoint')} dataKey="call" width={300} flexGrow={1} />
                    <Column
                      label={t('api:productCount')}
                      dataKey="item_count"
                      width={100}
                      flexGrow={1}
                    />
                  </Table>

                  <Spin
                    spinning={fetchingLogs && apiSettings.logs.length > 0}
                    className="spinner"
                    style={{
                      left: width / 2,
                      position: 'absolute',
                      bottom: '20px',
                    }}
                  />
                </React.Fragment>
              )}
            </InfiniteLoader>
          </div>
          <ApiRequestDetailsModal
            showModal={showModal}
            params={selectedParams}
            onClose={() => setShowModal(false)}
          />
        </div>
      )}
    </AutoSizer>
  );
};

export default ApiSettingsTable;
